import type { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { authSelectors } from '../../flux/auth';
import store from '../../utils/redux-store';
import type { ResourceSelectors } from './useLoadState';

export type FetchPossible = boolean | string | null | undefined;
export type FetchResourceAction = UnknownAction | ThunkAction<unknown, unknown, unknown, UnknownAction>;
export type FetchResourceActionCreator = () => FetchResourceAction;
type FetchResourceDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

// resourceSelectors should contain following methods to be fully compatible with this hook
// getLoadPending
// getIsLoading
// getError

export function isFetchPossible(ignoreDataset = false): FetchPossible {
  const authSession = authSelectors.getAuthSession(store!.getState());
  const datasetId = authSession?.datasetId;
  const isLoggedIn = Boolean(authSession?.accessToken);

  return isLoggedIn && (ignoreDataset || datasetId);
}

export function isFetchRequired(resourceSelectors: ResourceSelectors, update?: boolean): boolean {
  const state = store!.getState();
  verify(Boolean(resourceSelectors.getIsLoading), 'getIsLoading selector is not defined');
  const loading = resourceSelectors.getIsLoading ? resourceSelectors.getIsLoading(state) : false;
  if (!loading) {
    if (
      update ||
      (resourceSelectors.getLoadPending ? resourceSelectors.getLoadPending(state) : true) ||
      (resourceSelectors.getError ? resourceSelectors.getError(state) : undefined)
    ) {
      return true;
    }
  }
  return false;
}

export function isReadyToFetch(
  resourceSelectors: ResourceSelectors,
  update = true,
  ignoreDataset = false,
): FetchPossible {
  return isFetchPossible(ignoreDataset) && isFetchRequired(resourceSelectors, update);
}

export function fetchOnce(
  fetchResourceActionCreator: FetchResourceActionCreator,
  resourceSelectors: ResourceSelectors = {},
  update = true,
  ignoreDataset = false,
): void {
  if (isReadyToFetch(resourceSelectors, update, ignoreDataset)) {
    (store!.dispatch as FetchResourceDispatch)(fetchResourceActionCreator());
  }
}

export default function useFetchOnce(
  fetchResourceActionCreator: FetchResourceActionCreator,
  resourceSelectors: ResourceSelectors = {},
  update = true,
  ignoreDataset = false,
  enabled = true,
): void {
  const authSession = useSelector(authSelectors.getAuthSession);
  const datasetId = authSession?.datasetId;
  const isLoggedIn = Boolean(authSession?.accessToken);
  const fetchPossible = enabled && isLoggedIn && (ignoreDataset || datasetId);

  useEffect(() => {
    if (fetchPossible && isFetchRequired(resourceSelectors, update)) {
      (store!.dispatch as FetchResourceDispatch)(fetchResourceActionCreator());
    }
  }, [fetchResourceActionCreator, resourceSelectors, update, fetchPossible]);
}
